import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { campaignMobile } from '../../utils/campaignBreakpoints';
import Text from '../../../../components/Text';
import { ICampaign, DEFAULT_SEQUENCE } from '../../../../interfaces/models/campaign';
import { IProxyAccountDetails } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { formatSequenceSummary } from '../../../../utils/campaignSequenceSummary';
import {
  formatInMailCreditBalance,
  summarizeInMailCredits,
} from '../../utils/inMailCredits';
import SvgFragment from '../../../../fragments/SvgFragment';
import { useExternalHandlers } from '../../../../services/actions/navigation/useExternalHandlers';

const HEALTHY_PROXY_STATUSES = new Set(['connected', 'pending', 'error']);

interface Props {
  campaign: ICampaign;
  totalLeads: number;
  proxyAccounts: Record<string, IProxyAccountDetails>;
  extensionInstalled: boolean;
  isLoggedToLinkedin: boolean;
  onStart: () => void;
  starting?: boolean;
  highlighted?: boolean;
}

const CampaignStartPanel: React.FC<Props> = ({
  campaign,
  totalLeads,
  proxyAccounts,
  extensionInstalled,
  isLoggedToLinkedin,
  onStart,
  starting = false,
  highlighted = false,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { visitChromeStore } = useExternalHandlers();
  const isProxy = (campaign.executionMethod ?? 'extension') === 'proxy';
  const sequence = campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE;
  const sequenceOk = sequence.length > 0;
  const sequenceSummary = formatSequenceSummary(sequence);
  const hasSnMessageStep = sequence.some(s => s.type === 'sn_message');
  const inMailCreditSummary = useMemo(
    () => (isProxy ? summarizeInMailCredits(Object.values(proxyAccounts)) : null),
    [isProxy, proxyAccounts],
  );
  const usesPaidInMail = sequence.some(
    s => s.type === 'sn_message' && s.snNonOpenFallback === 'inmail_if_credits',
  );

  const accountIds = campaign.unipileConfig?.accountIds ?? [];
  const pendingIds = campaign.unipileConfig?.pendingAccountIds ?? [];
  const brokenProxy = Object.values(proxyAccounts).filter(a => !HEALTHY_PROXY_STATUSES.has(a.status));
  const proxyOk = accountIds.length > 0 && pendingIds.length === 0 && brokenProxy.length === 0;

  const extensionOk = extensionInstalled && isLoggedToLinkedin;
  const executionOk = isProxy ? proxyOk : extensionOk;

  const executionLabel = isProxy
    ? (proxyOk
      ? 'Cloud executor account connected'
      : pendingIds.length > 0
        ? 'Finish LinkedIn account verification in campaign settings'
        : accountIds.length === 0
          ? 'Connect a LinkedIn account in campaign settings'
          : 'Reconnect disconnected cloud account(s)')
    : (extensionOk
      ? 'LeadDelta extension connected'
      : !extensionInstalled
        ? null
        : 'Log in to LinkedIn in your browser');

  const handleInstallExtension = () => {
    try { sessionStorage.setItem('ld_extension_install_source', 'campaign_start_panel'); } catch {}
    visitChromeStore();
  };

  const renderExecutionCheckContent = () => {
    if (!isProxy && !extensionInstalled && !extensionOk) {
      return (
        <CheckLink type="button" onClick={handleInstallExtension}>
          Install the LeadDelta browser extension
        </CheckLink>
      );
    }
    return executionLabel;
  };

  // Leads are not a precondition: an empty campaign goes live and stays idle until
  // leads are enrolled, so the lead row is a status line rather than a blocker.
  const canStart = sequenceOk && executionOk;

  useEffect(() => {
    if (!highlighted || !panelRef.current) return;
    panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlighted]);

  return (
    <Panel ref={panelRef} $highlighted={highlighted}>
      <PanelHeader>
        <PanelIcon><SvgFragment type="campaignPlay" /></PanelIcon>
        <Column>
          <Text $bold style={{ fontSize: '15px', color: styles.colors.black600 }}>Ready to go live?</Text>
          <Text $paragraph3 style={{ color: styles.colors.black400, marginTop: '2px' }}>
            Your campaign is saved as a draft. Review the checklist below, then start outreach when you&apos;re ready.
          </Text>
        </Column>
      </PanelHeader>

      <Checklist>
        <CheckItem $ok={totalLeads > 0}>
          <CheckMark $ok={totalLeads > 0}>{totalLeads > 0 ? '✓' : '○'}</CheckMark>
          <span>
            {totalLeads > 0
              ? <><strong>{totalLeads}</strong> lead{totalLeads === 1 ? '' : 's'} enrolled</>
              : 'No leads yet — you can start now and add leads any time; sending begins when they arrive'}
          </span>
        </CheckItem>
        <CheckItem $ok={sequenceOk}>
          <CheckMark $ok={sequenceOk}>{sequenceOk ? '✓' : '○'}</CheckMark>
          <span>Sequence: {sequenceSummary}</span>
        </CheckItem>
        <CheckItem $ok={executionOk}>
          <CheckMark $ok={executionOk}>{executionOk ? '✓' : '○'}</CheckMark>
          <span>{renderExecutionCheckContent()}</span>
        </CheckItem>
        {isProxy && hasSnMessageStep && (
          <CheckItem $ok={!!inMailCreditSummary || !usesPaidInMail}>
            <CheckMark $ok={!!inMailCreditSummary || !usesPaidInMail}>
              {inMailCreditSummary || !usesPaidInMail ? '✓' : '○'}
            </CheckMark>
            <span>
              {inMailCreditSummary ? (
                <>
                  InMail credits: <strong>{formatInMailCreditBalance(inMailCreditSummary)}</strong>
                  {' · '}Open Profile messages are free
                </>
              ) : usesPaidInMail ? (
                'InMail credits unavailable — paid sends may pause until credits can be read'
              ) : (
                'Open Profile only — InMail credits not required'
              )}
            </span>
          </CheckItem>
        )}
      </Checklist>

      <StartBtn type="button" disabled={!canStart || starting} onClick={onStart}>
        <SvgFragment type="campaignPlay" />
        {starting ? 'Starting…' : 'Start campaign'}
      </StartBtn>
    </Panel>
  );
};

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(89, 100, 255, 0.25); }
  50% { box-shadow: 0 0 0 6px rgba(89, 100, 255, 0); }
`;

const Panel = styled.div<{ $highlighted?: boolean }>`
  margin: 0 24px 16px;
  padding: 20px;
  border-radius: 12px;
  border: 1.5px solid ${styles.colors.primary300};
  background: linear-gradient(180deg, ${styles.colors.primary100} 0%, ${styles.colors.white} 100%);
  ${campaignMobile`margin: 0 12px 12px; padding: 16px;`}
  ${({ $highlighted }) => $highlighted && css`
    animation: ${pulse} 2s ease-in-out 2;
  `}
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const PanelIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${styles.colors.primary600};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { width: 16px; height: 16px; filter: brightness(0) invert(1); }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Checklist = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckItem = styled.li<{ $ok: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: NunitoSans;
  font-size: 13px;
  color: ${({ $ok }) => $ok ? styles.colors.black600 : styles.colors.black400};
`;

const CheckMark = styled.span<{ $ok: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $ok }) => $ok ? styles.colors.green200 : styles.colors.black100};
  color: ${({ $ok }) => $ok ? styles.colors.green600 : styles.colors.black300};
`;

const CheckLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 600;
  color: ${styles.colors.primary600};
  text-decoration: underline;
  &:hover { color: ${styles.colors.primary400}; }
`;

const StartBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 24px;
  background: ${styles.colors.green600};
  color: ${styles.colors.white};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  font-family: NunitoSans;
  cursor: pointer;
  svg { width: 14px; height: 14px; filter: brightness(0) invert(1); }
  &:hover:not(:disabled) { background: ${styles.colors.green400}; }
  &:disabled {
    background: ${styles.colors.black200};
    color: ${styles.colors.black300};
    cursor: default;
    svg { filter: none; opacity: 0.5; }
  }
  ${campaignMobile`width: 100%; justify-content: center;`}
`;

export default CampaignStartPanel;
